/**
 * Move Simon vision (see §10). Lazy-loaded: NONE of this — including MediaPipe —
 * enters the Classic bundle. The MediaPipe import URL is intentionally
 * non-static so the bundler leaves it as a runtime CDN import.
 *
 * Pose heuristics are deliberately forgiving and gated by a confidence floor.
 * If the camera is denied or confidence stays low, the caller falls back to a
 * tap so the game never stalls (graceful degradation, §10/§12).
 *
 *   TUNING (move detection): MOVE_TUNING below.
 */

import type { MoveId } from '../shared/palette.js';

// CDN sources (pinned). Loaded once, lazily, only in Move Simon.
const VISION_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14';
const WASM_ROOT = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

export const MOVE_TUNING = {
  /** Per-landmark visibility floor; below this we treat the joint as unknown. */
  MIN_VISIBILITY: 0.5,
  /** Fraction of body height a hip must rise from baseline to count as a jump. */
  JUMP_RISE: 0.06,
  /** How close (normalised x) two wrists must get to count as a clap. */
  CLAP_GAP: 0.08,
  /** Min horizontal travel for a wave oscillation. */
  WAVE_TRAVEL: 0.05,
  /** Shoulder-width shrink ratio vs baseline that suggests a turn (spin). */
  SPIN_SHRINK: 0.55,
  /** Frames of history kept for temporal moves. */
  HISTORY: 30,
} as const;

// Pose landmark indices (MediaPipe BlazePose).
const L = { nose: 0, lsh: 11, rsh: 12, lel: 13, rel: 14, lwr: 15, rwr: 16, lhip: 23, rhip: 24 };

type Pt = { x: number; y: number; visibility?: number };

export interface MoveSession {
  stop(): void;
}

/**
 * Start the camera + pose detection for one move. Calls onDetect() once when
 * the move is recognised (caller then stops the session). onError() fires if
 * the camera/model can't start so the caller can fall back to a tap.
 */
export async function startMoveDetection(
  video: HTMLVideoElement,
  move: MoveId,
  onDetect: () => void,
  onError: (reason: string) => void,
): Promise<MoveSession> {
  let stopped = false;
  let raf = 0;
  let stream: MediaStream | null = null;
  let landmarker: any = null;

  const stop = () => {
    stopped = true;
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    try {
      landmarker?.close?.();
    } catch {
      /* ignore */
    }
  };

  try {
    // Lazy CDN import — non-static specifier keeps it out of the bundle.
    const vision: any = await import(/* @vite-ignore */ VISION_URL);
    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_ROOT);
    landmarker = await vision.PoseLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numPoses: 1,
    });

    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: 480, height: 640 },
      audio: false,
    });
    video.srcObject = stream;
    await video.play();
  } catch (e: any) {
    onError(e?.message || 'camera unavailable');
    stop();
    return { stop };
  }

  const detector = makeDetector(move);

  const loop = () => {
    if (stopped) return;
    if (video.readyState >= 2) {
      const res = landmarker.detectForVideo(video, performance.now());
      const lm: Pt[] | undefined = res?.landmarks?.[0];
      if (lm && detector(lm)) {
        onDetect();
        stop();
        return;
      }
    }
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
  return { stop };
}

/** Build a stateful per-move detector returning true when the move fires. */
function makeDetector(move: MoveId): (lm: Pt[]) => boolean {
  const hist: Pt[][] = [];
  const vis = (p?: Pt) => !!p && (p.visibility ?? 1) >= MOVE_TUNING.MIN_VISIBILITY;
  const shoulderY = (lm: Pt[]) => (lm[L.lsh].y + lm[L.rsh].y) / 2;
  const hipY = (lm: Pt[]) => (lm[L.lhip].y + lm[L.rhip].y) / 2;
  const shoulderW = (lm: Pt[]) => Math.abs(lm[L.lsh].x - lm[L.rsh].x);

  let baselineHipY = NaN;
  let baselineShoulderW = NaN;

  return (lm: Pt[]) => {
    hist.push(lm);
    if (hist.length > MOVE_TUNING.HISTORY) hist.shift();
    if (isNaN(baselineHipY) && vis(lm[L.lhip]) && vis(lm[L.rhip])) baselineHipY = hipY(lm);
    if (isNaN(baselineShoulderW) && vis(lm[L.lsh]) && vis(lm[L.rsh])) baselineShoulderW = shoulderW(lm);

    switch (move) {
      case 'armsup':
        // Both wrists above shoulders (smaller y = higher).
        return (
          vis(lm[L.lwr]) && vis(lm[L.rwr]) &&
          lm[L.lwr].y < shoulderY(lm) && lm[L.rwr].y < shoulderY(lm)
        );

      case 'jump': {
        // Hips rose meaningfully above baseline (body went up).
        if (isNaN(baselineHipY) || !vis(lm[L.lhip])) return false;
        return baselineHipY - hipY(lm) > MOVE_TUNING.JUMP_RISE;
      }

      case 'clap':
        // Wrists close together, around chest height.
        return (
          vis(lm[L.lwr]) && vis(lm[L.rwr]) &&
          Math.abs(lm[L.lwr].x - lm[L.rwr].x) < MOVE_TUNING.CLAP_GAP &&
          Math.abs(lm[L.lwr].y - lm[L.rwr].y) < MOVE_TUNING.CLAP_GAP * 1.5
        );

      case 'spin': {
        // Shoulders appear much narrower than baseline = turned sideways.
        if (isNaN(baselineShoulderW) || !vis(lm[L.lsh])) return false;
        return shoulderW(lm) < baselineShoulderW * MOVE_TUNING.SPIN_SHRINK;
      }

      case 'wave': {
        // One wrist above its shoulder AND swinging horizontally over history.
        const up = vis(lm[L.rwr]) && lm[L.rwr].y < lm[L.rsh].y ? L.rwr
          : vis(lm[L.lwr]) && lm[L.lwr].y < lm[L.lsh].y ? L.lwr : -1;
        if (up < 0 || hist.length < 6) return false;
        const xs = hist.slice(-12).map((h) => h[up]?.x).filter((x) => typeof x === 'number') as number[];
        if (xs.length < 6) return false;
        return Math.max(...xs) - Math.min(...xs) > MOVE_TUNING.WAVE_TRAVEL;
      }
    }
  };
}
